import { formatMontant } from '@mairistem/finances-utils';
import type ImputationNonTrouvee from '../../interfaces/simulation/imputationNonTrouvee';
import type ServiceNonHabilite from '../../interfaces/simulation/serviceNonHabilite';
import { getLibelleCodesImputation } from '../imputation';

// Contenu du "resultat" (JSON) d'un job d'import APCP ou de reprise des RAR
export interface ResultatRapport {
	imputationsNonTrouvees: ImputationNonTrouvee[];
	// Restes a realiser repris sur "Aucun service" (reprise des RAR seulement)
	servicesNonHabilites: ServiceNonHabilite[];
}

/**
 * Lit le "resultat" (JSON) d'un job d'import APCP ou de reprise des RAR termine
 * avec succes. Une liste est vide quand il n'y a pas de resultat a lire :
 * - null : back pas encore a jour, qui ne renseigne pas le resultat
 * - undefined : recopie des reports, qui utilise la meme modal sans transmettre de resultat
 * - cle absente : back pas encore a jour, ou import APCP sans servicesNonHabilites
 */
export const parseResultatRapport = (resultat?: unknown): ResultatRapport => {
	const parsed = typeof resultat === 'string' ? JSON.parse(resultat) : {};

	return {
		imputationsNonTrouvees: parsed.imputationsNonTrouvees ?? [],
		servicesNonHabilites: parsed.servicesNonHabilites ?? [],
	};
};

/**
 * Resume des lignes ignorees, par exemple : "Import effectué.\n2 lignes APCP n'ont
 * pas été importées : aucune imputation équivalente dans l'exercice 2026."
 * @param traitement Debut du message, par exemple "Import effectué."
 * @param anneeExercice Annee de l'exercice de la simulation
 */
export const getResumeImputationsNonTrouvees = (
	imputations: ImputationNonTrouvee[],
	traitement: string,
	anneeExercice?: number,
): string => {
	const count = imputations.length;
	// Rapport ouvert pour une autre raison (RAR repris sur "Aucun service")
	if (count === 0) return traitement;

	const lignes =
		count > 1
			? `${count} lignes APCP n'ont pas été importées`
			: "1 ligne APCP n'a pas été importée";
	const exercice = anneeExercice ? `l'exercice ${anneeExercice}` : "l'exercice";

	return `${traitement}\n${lignes} : aucune imputation équivalente dans ${exercice}.`;
};

// Les deux listes du resultat portent les memes codes d'imputation
const getCodesImputation = (ligne: ImputationNonTrouvee | ServiceNonHabilite) =>
	getLibelleCodesImputation(
		ligne.sens,
		ligne.section,
		ligne.chapitre,
		ligne.article,
		ligne.operation,
		ligne.fonction,
		ligne.ventilation,
		ligne.analytique,
	);

// Par exemple : "D F 011 6999 01 / Service voirie - AP 2 - Rénovation école" (HCV/HCA sont masques)
export const getLibelleImputationNonTrouvee = (
	imputation: ImputationNonTrouvee,
): string => {
	// Le service suit l'imputation, comme dans le rapport du transfert en comptabilite
	const imputationService = [
		getCodesImputation(imputation),
		imputation.libelleService,
	]
		.filter(Boolean)
		.join(' / ');

	return `${imputationService} - ${imputation.libelleProjet}`;
};

// La reprise se fait sur un BP, un BS ou une DM : le budget n'est pas nomme
export const getResumeServicesNonHabilites = (
	services: ServiceNonHabilite[],
): string =>
	services.length > 1
		? `${services.length} restes à réaliser ont été repris sur « Aucun service » : leur service n'est plus habilité sur la ligne.`
		: "1 reste à réaliser a été repris sur « Aucun service » : son service n'est plus habilité sur la ligne.";

// Par exemple : "D I 20 2031 OPNI 313 / SPORT : 200,00", le service suit
// l'imputation comme dans les autres rapports
export const getLibelleServiceNonHabilite = (
	service: ServiceNonHabilite,
): string =>
	`${getCodesImputation(service)} / ${service.libelleService ?? 'Service supprimé'} : ${formatMontant(service.rep)}`;
